import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Modal } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { FormInputText } from '../../../../form-components/FormInputText';
import CommonModalTitle from '../../../../form-components/CommonModalTitle';
import CommonText from '../../../../form-components/CommonText';
import CommonModalButton from '../../../../form-components/CommonModalButton';
import { createPointCategoryByUserId, updatePointCategoryById } from '../../../../api/point';
import { errorMessages } from '../../../../utils/methods';

// Styled Components
const ModalContainer = styled(Box)({
  backgroundColor: '#fff',
  borderRadius: '8px',
  width: '480px',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  overflow: 'hidden',
});

const ModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: '#fff',
  background: '#262626',
  height: '48px',
  padding: '0 20px',
});

const StyledCloseIcon = styled(CloseIcon)({
  cursor: 'pointer',
  fontSize: '20px',
});

const ModalContent = styled(Box)({
  padding: '24px 20px',
});

const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const ModalFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '16px 20px',
});

const FooterRightGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginLeft: 'auto',
});

// Main Component
const CategoryEditModal = ({
  open,
  onClose,
  mode,
  branchId,
  user,
  activeCategoryId,
  defaultCategoryName,
  onDelete,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      categoryName: defaultCategoryName || '',
    },
  });

  const isEditMode = mode === 'edit';

  // 모달이 열릴 때 폼 리셋
  React.useEffect(() => {
    if (open) {
      reset({ categoryName: defaultCategoryName || '' });
    }
  }, [open, defaultCategoryName, reset]);

  // 카테고리 생성 mutation
  const createMutation = useMutation({
    mutationFn: values => createPointCategoryByUserId(branchId, user.token, values),
    onSuccess: () => {
      toast.success(t('카테고리 생성이 완료되었습니다.'));
      queryClient.invalidateQueries({ queryKey: ['categories', branchId] });
      queryClient.invalidateQueries({ queryKey: ['products', branchId] });
      onClose();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 카테고리 수정 mutation
  const updateMutation = useMutation({
    mutationFn: values => updatePointCategoryById(user.token, activeCategoryId, values),
    onSuccess: () => {
      toast.success(t('카테고리 수정이 완료되었습니다.'));
      queryClient.invalidateQueries({ queryKey: ['categories', branchId] });
      queryClient.invalidateQueries({ queryKey: ['products', branchId] });
      onClose();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 모달 제출 핸들러
  const handleModalSubmit = values => {
    if (isEditMode) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="category-modal-title">
      <ModalContainer>
        <ModalHeader>
          <CommonModalTitle>{isEditMode ? t('일반 상품 분류 수정') : t('일반 상품 분류 추가')}</CommonModalTitle>
          <StyledCloseIcon onClick={onClose} />
        </ModalHeader>

        <ModalContent>
          <FormRow>
            <CommonText variant="label" sx={{ minWidth: '40px' }}>
              {t('분류명')}
            </CommonText>
            <FormInputText
              color="info"
              control={control}
              sx={{ flex: 1 }}
              name="categoryName"
              placeholder={t('분류명을 입력하여 주세요')}
            />
          </FormRow>
        </ModalContent>

        <ModalFooter>
          {isEditMode && (
            <CommonModalButton variant="delete" onClick={onDelete}>
              {t('삭제')}
            </CommonModalButton>
          )}
          <FooterRightGroup>
            <CommonModalButton variant="cancel" onClick={onClose}>
              {t('취소')}
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleSubmit(handleModalSubmit)} disabled={isLoading}>
              {isEditMode ? t('저장') : t('추가')}
            </CommonModalButton>
          </FooterRightGroup>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};

export default CategoryEditModal;
